import * as THREE from 'three';

/**
 * The `MouseTracker` class handles mouse tracking, including movement and clicks,
 * and provides raycasting functionalities to detect object intersections in a Three.js scene.
 * @category Experience
 * @subcategory World
 */
class MouseTracker {
  /**
   * Creates an instance of `MouseTracker`.
   * @param {object} experience - The experience object containing camera and renderer instances.
   */
  constructor(experience) {
    this.camera = experience.camera.instance;
    this.renderer = experience.renderer.instance;
    this.mouse = new THREE.Vector2();
    this.lastMouseUp = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();
    this.eventHub = experience.eventHub;  // ✅ Store eventHub reference

    this.init();
}


  cleanup() {
    try {
      if (this.renderer && this.renderer.domElement) {
        this.renderer.domElement.removeEventListener('mousemove', this.onMouseMove.bind(this));
        this.renderer.domElement.removeEventListener('mouseup', this.onMouseUp.bind(this));
      }
  
      this.mouse = null;
      this.lastMouseUp = null;
      this.raycaster = null;
      this.camera = null;
      this.renderer = null;
  
      console.log("MouseTracker cleanup completed.");
    } catch (error) {
      console.error("Error during MouseTracker cleanup:", error);
    }
  }

  /**
   * Initializes event listeners for mouse movement and mouse up events.
   */
  init() {
    this.renderer.domElement.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.renderer.domElement.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  /**
   * Updates the mouse position in normalized device coordinates (-1 to +1).
   * @param {MouseEvent} event - The mouse move event.
   */
  onMouseMove(event) {
    this.mouse.x = (event.clientX / this.renderer.domElement.clientWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / this.renderer.domElement.clientHeight) * 2 + 1;
    
    if (this.eventHub) {
        this.eventHub.emit("needsRender", "true");  // ✅ Request render on mouse move
    }
}


  /**
   * Stores the position where the mouse was last released.
   * @param {MouseEvent} event - The mouse up event.
   */
  onMouseUp(event) {
    this.lastMouseUp.copy(this.mouse); // Store the position where the mouse was last released

    if (this.eventHub) {
        this.eventHub.emit("needsRender", "true");  // ✅ Ensure render updates on mouse click release
    }
}


  /**
   * Performs raycasting to find intersections with the provided objects.
   * @param {THREE.Object3D[]} objects - The list of objects to check for intersections.
   * @returns {THREE.Intersection[]} - An array of intersection results.
   */
  getIntersection(objects) {
    this.raycaster.setFromCamera(this.mouse, this.camera);
    return this.raycaster.intersectObjects(objects, true);
  }

  /**
   * Retrieves the last mouse up position in normalized device coordinates.
   * @returns {THREE.Vector2} - The last mouse up position.
   */
  getLastMouseUpPosition() {
    return this.lastMouseUp.clone(); // Return the last mouse up position
  }

  /**
   * Retrieves the current mouse position in normalized device coordinates.
   * @returns {THREE.Vector2} - The current mouse position.
   */
  getCurrentMousePosition() {
    return this.mouse.clone(); // Return the current mouse position
  }
}

export default MouseTracker;
